import React, { FC }                                                                                                             from 'react';
import * as Yup                                                                                                                  from "yup";
import { BulkUpdateFormControlContainer }                                                                                        from "@src/views/pages/projects/activities/bulk/BulkUpdateFormControlContainer";
import { DateField, FieldDescription, FieldWrapper }                                                                             from "@src/views/components/forms";
import { StaticProjectActivityBulkUpdateShell, StaticProjectActivityBulkUpdateShellFormContainerProps, StaticProjectActivityBulkUpdateShellProps } from "@src/views/pages/projects/activities/bulk/StaticProjectActivityBulkUpdateShell";
import { ProjectActivityBulkUpdateMultiDialogProps }                                                                             from "@src/views/pages/projects/operations/StaticProjectActivityBulkUpdateMultiDialog";
import { TimeService }                                                                                                           from "@src/services";
import { Cell, Grid }                                                                                                            from "styled-css-grid";
import { FieldLabel }                                                                                                            from "@src/views/components/forms/formik/FieldLabel";
import { FinePrintMessage }                                                                                                      from "@src/views/components/util/messaging/FinePrintMessage";
import { TimingConstraintDropdown }                                                                                              from "@src/views/components/forms/dropdowns/TimingConstraintDropdown";
import { formatOption }                         from "@src/util";
import { ENTITY_ICONS, SELECTFIELD_NONE_VALUE } from "@src/constants";
import _                                        from "underscore";
import { useYupExtensions }                                                                                                      from "@src/hooks";


type FormTypeAttributes = {
    startDate: string | null
    endDate: string | null
    deadlineDate: string | null
    constraintDate: string | null
    constraintType: string | null
    manuallyScheduled: string | null
}

const formDefaultValues = {
    startDate:         '',
    endDate:           '',
    deadlineDate:      '',
    constraintDate:    '',
    constraintType:    '',
    manuallyScheduled: '',
}

const validationSchema = () => (
    Yup.object().shape( {
        startDate:      Yup.string()
                           .when( 'startDate', {
                               is:   values => !_.isEmpty( values ),
                               then: Yup.string()
                                        .isDateFormat()
                                        .test( {
                                            name:    'Ensure start date is before end date',
                                            message: `Start date must come before end date.`,
                                            test( _startDate ){

                                                let _endDate = this.parent.endDate ?? null;

                                                if( _.isEmpty( _startDate ) || _.isEmpty( _endDate ) ) return true;

                                                let endDate   = TimeService.displayDateToMoment( _endDate ),
                                                    startDate = TimeService.displayDateToMoment( _startDate );

                                                return startDate.isBefore( endDate );
                                            }
                                        } ),
                           } ),
        endDate:        Yup.string()
                           .when( 'endDate', {
                               is:   values => !_.isEmpty( values ),
                               then: Yup.string().isDateFormat(),
                           } ),
        constraintType: Yup.string()
                           .test( {
                               name:    'Ensure constraint date is specified',
                               message: `Constraint date must be specified.`,
                               test( _constraintType ){

                                   let _constraintDate = this.parent.constraintDate ?? null;

                                   if( _.isEmpty( _constraintType ) || _constraintType === SELECTFIELD_NONE_VALUE ) return true;

                                   return !_.isEmpty( _constraintDate );
                               }
                           } ),
        constraintDate: Yup.string()
                           .when( 'constraintDate', {
                               is:   values => !_.isEmpty( values ),
                               then: Yup.string()
                                        .isDateFormat()
                                        .test( {
                                            name:    'Ensure constraint type is specified',
                                            message: `Constraint type must be specified.`,
                                            test( _constraintDate ){

                                                let _constraintType = this.parent.constraintType ?? null;

                                                if( _.isEmpty( _constraintDate ) ) return true;

                                                return !_.isEmpty( _constraintType ) || _constraintType === SELECTFIELD_NONE_VALUE;
                                            }
                                        } ),
                           } ),
        deadlineDate:   Yup.string()
                           .when( 'deadlineDate', {
                               is:   values => !_.isEmpty( values ),
                               then: Yup.string().isDateFormat(),
                           } ),
    }, [
        [ 'startDate', 'endDate' ],
        [ 'startDate', 'startDate' ],
        [ 'endDate', 'endDate' ],
        [ 'constraintDate', 'constraintDate' ],
        [ 'deadlineDate', 'deadlineDate' ],
    ] )
)

const mutationAttributeFormatter: StaticProjectActivityBulkUpdateShellProps<FormTypeAttributes>['prepareFormValuesForMutation'] = ( values, formik ) => {

    const {
              startDate,
              endDate,
              deadlineDate,
              constraintDate,
              constraintType,
          } = values

    const changes: Record<any, any> = {};

    if( !_.isEmpty( startDate ) && formik.initialValues.startDate != startDate ){
        changes.startDate = TimeService.displayDateToLocalDate( startDate );
    }

    if( !_.isEmpty( endDate ) && formik.initialValues.endDate != endDate ){
        changes.endDate = TimeService.displayDateToLocalDate( endDate );
    }

    if( !_.isEmpty( deadlineDate ) && formik.initialValues.deadlineDate != deadlineDate ){
        changes.deadlineDate = TimeService.displayDateToLocalDate( deadlineDate );
    }

    if( !_.isEmpty( constraintDate ) && formik.initialValues.constraintDate != constraintDate ){
        changes.constraintDate = TimeService.displayDateToLocalDate( constraintDate );
    }

    if( !_.isEmpty( constraintType ) && formik.initialValues.constraintType != constraintType ){

        if( constraintType === SELECTFIELD_NONE_VALUE ){
            changes.constraintType = null;
            changes.constraintDate = null;
        } else {
            changes.constraintType = constraintType;
        }
    }

    return changes;
}

interface StaticProjectActivityBulkUpdateTimingProps
    extends Pick<StaticProjectActivityBulkUpdateShellProps<FormTypeAttributes>, 'supportModals' | 'supportToasts'>,
        Pick<ProjectActivityBulkUpdateMultiDialogProps, 'projectId' | 'projectVersionId' | 'projectActivityIds'> {
    formContainer: FC<StaticProjectActivityBulkUpdateShellFormContainerProps>
}

const StaticProjectActivityBulkUpdateTimingDefaultProps = {
    supportModals: false,
    supportToasts: true,
} satisfies Partial<StaticProjectActivityBulkUpdateTimingProps>

function StaticProjectActivityBulkUpdateTiming( _props: StaticProjectActivityBulkUpdateTimingProps ){

    const props = { ...StaticProjectActivityBulkUpdateTimingDefaultProps, ..._props };

    const { addStringIsDateFormatTest } = useYupExtensions();

    addStringIsDateFormatTest();

    return (

        <StaticProjectActivityBulkUpdateShell<FormTypeAttributes>
            projectId={ props.projectId }
            projectVersionId={ props.projectVersionId }
            ids={ props.projectActivityIds }
            supportToasts={ props.supportToasts }
            supportModals={ props.supportModals }
            defaultFormValues={ formDefaultValues }
            validationSchema={ validationSchema() }
            formContainer={ props.formContainer }
            prepareFormValuesForMutation={ mutationAttributeFormatter }>

            {/* FORM CONTENT */ }
            { ( { formik, isProcessing, isLoading } ) => (

                <BulkUpdateFormControlContainer
                    icon={ ENTITY_ICONS.ACTIVITIES.TIMING.NORMAL }
                    title={ 'Update Timing' }
                    description={ 'Migrate dates and constraints for multiple activities to the specified timing.' }>

                    {/**/ }
                    <Grid columns={ 'auto' } rows={ 'auto' } rowGap={ '8px' }>

                        <Cell>

                            <Grid columns={ `1fr 1fr` } rows={ `repeat( 4, auto )` } rowGap={ '20px' } columnGap={ `18px` } areas={ [
                                'start end',
                                'constraintDesc constraintDesc',
                                'constraintType constraintDate',
                                'deadline .',
                            ] }>

                                {/* Start Date Controls */ }
                                <Cell area={ 'start' }>

                                    <FieldWrapper>
                                        <FieldLabel>Start Date</FieldLabel>
                                        <FieldDescription>
                                            <FinePrintMessage as={ 'span' }>The scheduled start date for the selected activities, if no other scheduling concerns are involved.</FinePrintMessage>
                                        </FieldDescription>
                                    </FieldWrapper>

                                    <DateField
                                        name={ 'startDate' }
                                        disabled={ isLoading || isProcessing }
                                    />
                                </Cell>

                                {/* End Date Control */ }
                                <Cell area={ 'end' }>

                                    <FieldWrapper>
                                        <FieldLabel>End Date</FieldLabel>
                                        <FieldDescription>
                                            <FinePrintMessage as={ 'span' }>The scheduled end date for the selected activities, if no other scheduling concerns are involved.</FinePrintMessage>
                                        </FieldDescription>
                                    </FieldWrapper>

                                    <DateField
                                        name={ 'endDate' }
                                        disabled={ isLoading || isProcessing }
                                    />
                                </Cell>

                                {/* Constraint Controls */ }
                                <Cell area={ 'constraintDesc' }>

                                    <FieldWrapper>
                                        <FieldLabel>Constraint Type</FieldLabel>
                                        <FieldDescription>
                                            <FinePrintMessage as={ 'span' }>Optional. Regardless of start date, you can specify timing the selected activities will try to honor.</FinePrintMessage>
                                        </FieldDescription>
                                    </FieldWrapper>

                                </Cell>

                                <Cell area={ 'constraintType' }>

                                    <TimingConstraintDropdown
                                        name={ 'constraintType' }
                                        disabled={ isLoading || isProcessing }
                                        preventFirstSelection={ false }
                                        prepend={ [
                                            formatOption( SELECTFIELD_NONE_VALUE, 'None' ),
                                        ] }
                                        onChange={ ( value ) => {
                                            if( value === SELECTFIELD_NONE_VALUE ){
                                                formik.setFieldValue( 'constraintDate', '' );
                                            }
                                        } }
                                    />

                                </Cell>

                                {/* Deadline Control */ }
                                <Cell area={ 'constraintDate' }>

                                    <DateField
                                        name={ 'constraintDate' }
                                        disabled={ isLoading || isProcessing || ( formik.values.constraintType === SELECTFIELD_NONE_VALUE ) }
                                        onAfterChange={ ( value ) => {
                                            if( _.isEmpty( value ) ){
                                                formik.setFieldValue( 'constraintType', SELECTFIELD_NONE_VALUE );
                                            }
                                        } }
                                    />

                                </Cell>

                                {/* Deadline Control */ }
                                <Cell area={ 'deadline' }>

                                    <FieldWrapper>
                                        <FieldLabel>Deadline</FieldLabel>
                                        <FieldDescription>
                                            <FinePrintMessage as={ 'span' }>Specific timing the selected activities should complete. Does not affect scheduling.</FinePrintMessage>
                                        </FieldDescription>
                                    </FieldWrapper>

                                    <DateField
                                        name={ 'deadlineDate' }
                                        disabled={ isLoading || isProcessing }
                                    />

                                </Cell>

                            </Grid>

                        </Cell>

                    </Grid>
                    {/**/ }

                </BulkUpdateFormControlContainer>

            ) }

        </StaticProjectActivityBulkUpdateShell>

    );
}

export {
    StaticProjectActivityBulkUpdateTiming,
    StaticProjectActivityBulkUpdateTimingDefaultProps,
    type StaticProjectActivityBulkUpdateTimingProps,
};
